/**
 * Error Tracking Service (Web)
 * Sentry integration for crash reporting and error monitoring
 */

import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  Routes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

/**
 * Initialize Sentry error tracking
 * Call this once at app startup (in main.tsx or App.tsx)
 */
export function initErrorTracking() {
  const isDevelopment = import.meta.env.DEV;

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN || '',
    enabled: !isDevelopment,
    environment: isDevelopment ? 'development' : 'production',

    // Performance monitoring
    tracesSampleRate: 1.0, // 100% of transactions in production (adjust for high traffic)
    tracePropagationTargets: ['localhost', /^https:\/\/web-production-55e84\.up\.railway\.app/],

    // React-specific integrations
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: true,
      }),
    ],

    // Session replay sampling
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Drop fetch/network errors ONLY when the browser confirms the user is offline.
    // Online + "failed to fetch" = real signal (API down, TLS, CORS, SSE drop) — keep it.
    beforeSend(event, hint) {
      const error = hint.originalException;
      if (error && typeof error === 'object' && 'message' in error) {
        const message = String(error.message).toLowerCase();
        const isNetworkFailureMessage =
          message.includes('network request failed') || message.includes('failed to fetch');
        const userIsOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
        if (isNetworkFailureMessage && userIsOffline) {
          return null;
        }
      }
      return event;
    },
  });
}

/**
 * Manually log an error to Sentry
 */
export function logError(error: Error, context?: Record<string, unknown>) {
  Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Log a custom message (info, warning, error)
 */
export function logMessage(
  message: string,
  level: 'info' | 'warning' | 'error' = 'info',
  context?: Record<string, unknown>,
) {
  Sentry.captureMessage(message, {
    level,
    extra: context,
  });
}

/**
 * Set user context for error tracking
 * Call this after successful login
 */
export function setUserContext(user: {
  id: string;
  email: string;
  role?: string;
  emailVerified?: boolean;
}) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    subscription_tier: user.role,
    role: user.role,
    email_verified: String(user.emailVerified ?? false),
  });
}

/**
 * Clear user context
 * Call this on logout
 */
export function clearUserContext() {
  Sentry.setUser(null);
}

/**
 * Add a breadcrumb for debugging context
 */
export function addBreadcrumb(message: string, category: string, data?: Record<string, unknown>) {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  });
}

/**
 * Start a performance span
 * Use Sentry.startSpan() for modern transaction tracking
 */
export function startTransaction(name: string, op: string) {
  // Modern Sentry uses startSpan instead of startTransaction
  // For backwards compatibility, we'll use the deprecated API if available
  // @ts-expect-error - startTransaction may not exist in newer Sentry versions
  if (typeof Sentry.startTransaction === 'function') {
    // @ts-expect-error
    return Sentry.startTransaction({ name, op });
  }
  // Fallback: return a no-op object
  return { finish: () => {} };
}

/**
 * Wrap React Router to enable performance monitoring
 */
export const SentryRoutes = Sentry.withSentryRouting(Routes);
